import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Button, Alert, AlertTitle, Collapse, IconButton, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Swal from 'sweetalert2';
import PCNChecksSummary from './PCNChecksSummary';
import PCNChecksAccordion from './PCNChecksAccordion';
import DocumentService from '../../Services/documents.service';
import PCNService from '../../Services/pcn.service';
import TaxAuthorityService from '../../Services/taxAuthority.service';
import EnumsService from '../../Services/enums.service';
import Constants from '../../assests/Constants/constants';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import { toast } from 'react-toastify';
import { beautifyNumber } from '../../Utils/FormatNumber';
import { translateShaamMessage } from '../../Utils/shaamMessages';

const PCNChecksModal = ({
    open,
    onClose,
    direction,
    t,
    pcnHighConfirmationDocs,
    pcnDocumentsNeedingAllocation,
    pcnDocumentsWithoutReference,
    pcnDocumentsOutOfRange,
    pcnReportDetails,
    onDocumentClick,
    setShowPCNModal,
    handlePCNDownload,
    startDate,
    endDate,
    caseId: propCaseId,
    token: propToken,
    includeStornoChecks = null,
    viewDocumentDetails,
    sortCodeIdOptions = [],
    accountCardOptions = [],
}) => {
    const { menuProps } = useHamburgerMenu();
    const [referenceContinuityIssues, setReferenceContinuityIssues] = useState(null);
    const [referenceCheckLoading, setReferenceCheckLoading] = useState(false);
    const [showReferenceAlert, setShowReferenceAlert] = useState(true);
    const [sendVatReportLoading, setSendVatReportLoading] = useState(false);
    const [vatReportAction, setVatReportAction] = useState(2);   // 1 = report, 2 = check, 3 = check and report only if 100% correct
    const [resumeAfterAuth, setResumeAfterAuth] = useState(false);
    const [vatReportResult, setVatReportResult] = useState(null);
    const pendingActionRef = useRef(null);
    const [localSortCodeIdOptions, setLocalSortCodeIdOptions] = useState([]);
    const [localAccountCardOptions, setLocalAccountCardOptions] = useState([]);

    // Get caseId, accountantId and token from props or context (menuProps)
    const caseId = propCaseId || menuProps?.client?.caseId;
    const accountantId = menuProps?.client?.accountantId;
    const token = propToken || menuProps?.token;

    // Check reference continuity when modal opens or dates change
    useEffect(() => {
        if (!open || !caseId || !token || !startDate || !endDate) {
            if (!open) {
                setReferenceContinuityIssues(null);
                setVatReportResult(null);
                setLocalSortCodeIdOptions([]);
                setLocalAccountCardOptions([]);
            }
            return;
        }

        const checkReferenceContinuity = async () => {
            setReferenceCheckLoading(true);
            try {
                const startDateToCheck = startDate?.toDate?.() || startDate;
                const endDateToCheck = endDate?.toDate?.() || endDate;

                if (!startDateToCheck || !endDateToCheck) {
                    setReferenceContinuityIssues(null);
                    setReferenceCheckLoading(false);
                    return;
                }

                const result = await DocumentService.CheckReferenceContinuity(
                    caseId,
                    startDateToCheck,
                    endDateToCheck,
                    token
                );

                setReferenceContinuityIssues(result);
            } catch (error) {
                console.error('Error checking reference continuity:', error);
                setReferenceContinuityIssues(null);
            } finally {
                setReferenceCheckLoading(false);
            }
        };

        checkReferenceContinuity();
    }, [open, caseId, token, startDate, endDate]);

    // Reset alert visibility when modal opens
    useEffect(() => {
        if (open) {
            setShowReferenceAlert(true);
        }
    }, [open]);

    // Fallback: fetch sort codes and account cards when modal opens if parent did not pass them (so Debit/Credit show full labels)
    useEffect(() => {
        if (!open || !token) return;
        const needSortCodes = !sortCodeIdOptions?.length && accountantId;
        const needAccountCards = !accountCardOptions?.length && caseId;
        if (!needSortCodes && !needAccountCards) return;

        let cancelled = false;
        const fetchOptions = async () => {
            try {
                if (needSortCodes) {
                    const res = await EnumsService.GetSortCodesByAccountantId(accountantId, token);
                    if (res?.ok && !cancelled) {
                        const data = await res.json();
                        setLocalSortCodeIdOptions(Array.isArray(data) ? data : []);
                    }
                }
                if (needAccountCards) {
                    const res = await EnumsService.GetAccountCardsByCaseId(caseId, token);
                    if (res?.ok && !cancelled) {
                        const data = await res.json();
                        const list = Array.isArray(data) ? data : [];
                        setLocalAccountCardOptions(list.filter(opt => opt.cardId != null || opt.CardId != null));
                    }
                }
            } catch (e) {
                if (!cancelled) console.error('PCNChecksModal fallback fetch options:', e);
            }
        };
        fetchOptions();
        return () => { cancelled = true; };
    }, [open, token, caseId, accountantId, sortCodeIdOptions?.length, accountCardOptions?.length]);

    const handleVatReportActionChange = (newAction) => {
        setVatReportAction(newAction);
    };

    const getVatReportButtonLabel = () => {
        if (vatReportAction === 1) return t('VatReportActionReport');
        if (vatReportAction === 2) return t('VatReportActionCheck');
        return t('VatReportActionCheckAndReportIfCorrect');
    };

    // ── Token auth flow (same pattern as VatModal) ──────────────────────────────
    const startAuthCompletionCheck = (authWindow) => {
        let attempts = 0;
        const maxAttempts = 25;

        const checkAuthCompletion = async () => {
            try {
                const result = await TaxAuthorityService.checkAuthCompletionAccountant(
                    accountantId,
                    Constants.TaxAuthorityScopes['VatReport'],
                    token
                );
                if (result.completed) {
                    authWindow.close();
                    setResumeAfterAuth(true);
                } else {
                    attempts += 1;
                    if (attempts < maxAttempts) {
                        setTimeout(checkAuthCompletion, 3000);
                    } else {
                        setSendVatReportLoading(false);
                        toast.error(t('TokenRetrievalFailed') || 'Token retrieval failed after maximum attempts.');
                        authWindow.close();
                    }
                }
            } catch (err) {
                console.error('Error checking auth completion:', err);
                toast.error(t('ErrorCheckingAuthCompletion') || t('Error'));
                authWindow.close();
                setSendVatReportLoading(false);
            }
        };

        checkAuthCompletion();
    };

    // Resume sending after auth window completes
    useEffect(() => {
        if (resumeAfterAuth && pendingActionRef.current != null) {
            setResumeAfterAuth(false);
            sendVatDetailedReportToApi(pendingActionRef.current);
        }
    }, [resumeAfterAuth]);

    const sendVatDetailedReportToApi = async (action) => {
        setSendVatReportLoading(true);
        try {
            const dateVal = startDate?.toDate?.() || startDate;
            const endDateVal = endDate?.toDate?.() || endDate;
            const result = await PCNService.SendVatDetailedReporting({
                caseId,
                action,
                date: dateVal,
                endDate: endDateVal,
                includeStornoChecks,
            });
            const ri = result?.report_information ?? result?.reportInformation;
            const summaryFromResult = result?.report_summary ?? result?.reportSummary;
            setVatReportResult({
                ...result,
                _action: action,
                report_information: ri,
                report_summary: summaryFromResult,
            });
            const hasError = ri?.error_code != null && ri?.error_code !== 0;
            if (hasError) {
                const errMsg = ri.error_message || ri.errorMessage || result?.message || t('SendVatDetailedReportIncorrect');
                const errCode = ri.error_code ?? 'N/A';
                return;
            }
            // Report the authority's own wording rather than assuming "no incorrect invoices":
            // a report can come back with error_code 0 and still contain incorrect invoices.
            const successMsg = translateShaamMessage(result?.message, t) || t('SendVatDetailedReportSuccess');
            const showSummarySwal = summaryFromResult && (action === 1 || action === 3);
            if (showSummarySwal) {
                const summary = summaryFromResult;
                const exempt = summary.exempt_transactions ?? summary.exemptTransactions ?? 0;
                const taxable = summary.taxable_transactions ?? summary.taxableTransactions ?? 0;
                const transVat = summary.transaction_vat ?? summary.transactionVat ?? 0;
                const otherInput = summary.other_input_vat ?? summary.otherInputVat ?? 0;
                const equipInput = summary.equipment_input_vat ?? summary.equipmentInputVat ?? 0;
                const amountPayRefund = summary.amount_to_pay_or_refund ?? summary.amountToPayOrRefund ?? 0;
                const dir = direction === 'rtl' ? 'rtl' : 'ltr';
                const textAlign = direction === 'rtl' ? 'right' : 'left';
                const valueAlign = direction === 'rtl' ? 'left' : 'right';
                const detailsHtml = `<div dir="${dir}" style="margin-top: 12px; font-size: 13px;">
                  <table style="width: 100%; border-collapse: collapse;">
                    <tr style="background: #f0f2f5;"><td style="padding: 6px 8px; text-align: ${textAlign}">${t('TotalTaxableTransactions')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(taxable))} ש"ח</td></tr>
                    <tr><td style="padding: 6px 8px; text-align: ${textAlign}">${t('VATOnTaxableTransactions')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(transVat))} ש"ח</td></tr>
                    <tr style="background: #f0f2f5"><td style="padding: 6px 8px; text-align: ${textAlign}">${t('ExemptZeroTransactions')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(exempt))} ש"ח</td></tr>
                    <tr><td style="padding: 6px 8px; text-align: ${textAlign}">${t('EquipmentInputs')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(equipInput))} ש"ח</td></tr>
                    <tr style="background: #f0f2f5"><td style="padding: 6px 8px; text-align: ${textAlign}">${t('OtherInputs')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600">${beautifyNumber(Math.round(otherInput))} ש"ח</td></tr>
                    <tr><td style="padding: 6px 8px; text-align: ${textAlign}">${amountPayRefund > 0 ? t('VATToPay') : t('AmountForRefundTransmitted')}</td><td style="padding: 6px 8px; text-align: ${valueAlign}; font-weight: 600; ${amountPayRefund < 0 ? 'color: #d32f2f;' : ''}">${amountPayRefund < 0 ? `(${beautifyNumber(Math.abs(Math.round(amountPayRefund)))})` : beautifyNumber(Math.round(amountPayRefund))} ש"ח</td></tr>
                  </table>
                </div>`;
                const successIconHtml = '<div style="width:80px;height:80px;margin:0 auto 16px;border-radius:50%;background:#a5dc86;display:flex;align-items:center;justify-content:center"><svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg></div>';
                Swal.fire({
                    icon: 'success',
                    iconHtml: successIconHtml,
                    title: successMsg,
                    html: detailsHtml,
                    customClass: { container: 'swal-high-zindex', popup: direction === 'rtl' ? 'swal2-rtl' : '' },
                });
            } else {
                toast.success(successMsg);
            }
        } catch (err) {
            const errorPayload = err?.error ?? err?.details?.error ?? {};
            const code = errorPayload.code ?? errorPayload.statusCode ?? errorPayload.Code ?? errorPayload.StatusCode;
            if (code === 401 || code === '401') {
                toast.error(t('SendVatDetailedReportUnauthorized'), { autoClose: false });
                return;
            }
            // Transport failures carry no tax-authority error number, so the generic
            // "number + support message" toast would show the raw server text. Map them to their
            // own translated message instead.
            const transportErrorKeys = {
                504: 'SendVatDetailedReportTimeout',
                502: 'SendVatDetailedReportUnreachable',
                429: 'SendVatDetailedReportRateLimited',
            };
            const transportErrorKey = transportErrorKeys[Number(code)];
            if (transportErrorKey) {
                toast.error(t(transportErrorKey), { autoClose: false });
                return;
            }
            const errorNumber = errorPayload.errorNumber ?? errorPayload.ErrorNumber ?? 'N/A';
            const supportMessage = errorPayload.supportMessage ?? errorPayload.SupportMessage
                ?? errorPayload.errorDescription ?? errorPayload.ErrorDescription
                ?? (err?.message) ?? t('SendVatDetailedReportFailed');

            const errorMessage = err?.details?.message || err?.message || t('SendVatDetailedReportFailed');
            const detailedErrorMessage = `${errorMessage}\n\n❌ Error Number: ${errorNumber}\n💬 Support Message: ${supportMessage}`;
            toast.error(detailedErrorMessage, { autoClose: false });
        } finally {
            setSendVatReportLoading(false);
        }
    };

    const handleSendVatDetailedReport = async () => {
        if (!caseId) {
            toast.error(t('SendVatDetailedReportFailed') || t('Error'));
            return;
        }
        if (!accountantId) {
            toast.error(t('AccountantIdRequiredForVatReport') || t('SendVatDetailedReportFailed') || t('Error'));
            return;
        }

        const isReportAction = vatReportAction === 1 || vatReportAction === 3;
        if (isReportAction) {
            const { isConfirmed } = await Swal.fire({
                icon: 'warning',
                title: t('VatReportSendConfirmTitle') || 'Confirm send to tax authority',
                text: t('VatReportSendConfirmDescription') || 'This report will be sent to the tax authority and cannot be changed after submission. Do you want to continue?',
                showCancelButton: true,
                confirmButtonText: t('Confirm') || 'Confirm',
                cancelButtonText: t('Cancel') || 'Cancel',
                customClass: { container: 'swal-high-zindex' },
            });
            if (!isConfirmed) return;
        }

        pendingActionRef.current = vatReportAction;
        setSendVatReportLoading(true);

        try {
            const tokenData = await TaxAuthorityService.requestTokenAccountant(
                accountantId,
                Constants.TaxAuthorityScopes['VatReport'],
                token
            );

            if (tokenData?.authUrl) {
                const authWindow = window.open(tokenData.authUrl, '_blank');
                startAuthCompletionCheck(authWindow);
                return;
            }

            await sendVatDetailedReportToApi(vatReportAction);
        } catch (err) {
            console.error('Error in token request flow:', err);
            toast.error(t('ErrorFetchingToken') || t('SendVatDetailedReportFailed'));
            setSendVatReportLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="pcn-checks-modal-title"
            aria-describedby="pcn-checks-modal-description"
            sx={{ zIndex: 1300 }}
            disableEnforceFocus 
            disableAutoFocus
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                minWidth: 800,
                maxWidth: 1200,
                width: '90%',
                maxHeight: '80vh',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: 24,
                padding: 4,
                outline: 'none',
                overflow: 'auto'
            }} className={`modal-container ${direction}`}>
                
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 id="pcn-checks-modal-title" style={{ 
                        fontSize: '24px',
                        fontWeight: 600,
                        color: 'rgba(0, 0, 0, 0.90)',
                        margin: 0
                    }}>
                        {t('PCNDocumentsNeedingConfirmation')}
                    </h2>
                    <div className="client-back-icon" onClick={onClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M15.2501 4.75794C14.9251 4.43294 14.4001 4.43294 14.0751 4.75794L10.0001 8.82461L5.9251 4.74961C5.6001 4.42461 5.0751 4.42461 4.7501 4.74961C4.4251 5.07461 4.4251 5.59961 4.7501 5.92461L8.8251 9.99961L4.7501 14.0746C4.4251 14.3996 4.4251 14.9246 4.7501 15.2496C5.0751 15.5746 5.6001 15.5746 5.9251 15.2496L10.0001 11.1746L14.0751 15.2496C14.4001 15.5746 14.9251 15.5746 15.2501 15.2496C15.5751 14.9246 15.5751 14.3996 15.2501 14.0746L11.1751 9.99961L15.2501 5.92461C15.5668 5.60794 15.5668 5.07461 15.2501 4.75794Z" fill="black" fillOpacity="0.8" />
                        </svg>
                    </div>
                </div>

                {/* Reference Continuity Alert */}
                {referenceContinuityIssues && !referenceContinuityIssues.isContinuous && (
                    <>
                        {!showReferenceAlert && (
                            <Alert 
                                severity="warning" 
                                sx={{ mb: 2, cursor: 'pointer' }}
                                onClick={() => setShowReferenceAlert(true)}
                            >
                                <AlertTitle>{t('ReferenceContinuityIssues')}</AlertTitle>
                                <div style={{ fontSize: '14px' }}>
                                    {referenceContinuityIssues.issues?.length || 0} {t('Issues')} - {t('ClickToExpand') || 'Click to view details'}
                                </div>
                            </Alert>
                        )}
                        <Collapse in={showReferenceAlert}>
                            <Alert 
                                severity="warning" 
                                sx={{ mb: 2 }}
                                action={
                                    <IconButton
                                        aria-label="close"
                                        color="inherit"
                                        size="small"
                                        onClick={() => setShowReferenceAlert(false)}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                }
                            >
                            <AlertTitle>{t('ReferenceContinuityIssues')}</AlertTitle>
                            <div style={{ 
                                direction: direction, 
                                textAlign: direction === 'rtl' ? 'right' : 'left',
                                marginTop: '8px'
                            }}>
                                <p style={{ marginBottom: '8px' }}>{t('ReferenceContinuityWarning')}</p>
                                <div style={{
                                    background: '#fff3cd',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    marginTop: '10px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    direction: direction,
                                    textAlign: direction === 'rtl' ? 'right' : 'left',
                                    fontSize: '14px'
                                }}>
                                    <strong>{t('IssuesFound')}</strong>
                                    <ul style={{ marginTop: '8px', paddingRight: direction === 'rtl' ? '20px' : '0', paddingLeft: direction === 'ltr' ? '20px' : '0' }}>
                                        {referenceContinuityIssues.issues?.map((issue, index) => {
                                            let translatedDescription = issue.description;
                                            if (issue.issueType === 'Duplicate') {
                                                translatedDescription = t('Duplicate reference number') + ': ' + issue.actualReference;
                                            } else if (issue.issueType === 'Gap') {
                                                translatedDescription = t('Missing reference number') + ': ' + issue.expectedReference;
                                            } else if (issue.issueType === 'Invalid') {
                                                translatedDescription = t('Invalid reference format') + ': \'' + issue.actualReference + '\'';
                                            }
                                            return <li key={index} style={{ marginBottom: '4px' }}>{translatedDescription}</li>;
                                        })}
                                    </ul>
                                </div>
                                {viewDocumentDetails && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            onClose();
                                            viewDocumentDetails('Incomes');
                                        }}
                                        sx={{ mt: 1 }}
                                    >
                                        {t('ViewIncomes')}
                                    </Button>
                                )}
                            </div>
                        </Alert>
                    </Collapse>
                    </>
                )}

                {/* PCN Checks Summary */}
                {/* <PCNChecksSummary 
                    pcnHighConfirmationDocs={pcnHighConfirmationDocs}
                    pcnDocumentsNeedingAllocation={pcnDocumentsNeedingAllocation}
                    pcnDocumentsWithoutReference={pcnDocumentsWithoutReference}
                    pcnDocumentsOutOfRange={pcnDocumentsOutOfRange}
                    t={t}
                    direction={direction}
                    pcnCheckStartDate={startDate}
                /> */}

                {/* PCN Checks Accordion */}
                <PCNChecksAccordion 
                    pcnHighConfirmationDocs={pcnHighConfirmationDocs}
                    pcnDocumentsNeedingAllocation={pcnDocumentsNeedingAllocation}
                    pcnDocumentsWithoutReference={pcnDocumentsWithoutReference}
                    pcnDocumentsOutOfRange={pcnDocumentsOutOfRange}
                    pcnReportDetails={pcnReportDetails}
                    onDocumentClick={onDocumentClick}
                    vatReportResult={vatReportResult}
                    t={t}
                    direction={direction}
                    pcnCheckStartDate={startDate}
                    pcnCheckEndDate={endDate}
                    sortCodeIdOptions={sortCodeIdOptions?.length ? sortCodeIdOptions : localSortCodeIdOptions}
                    accountCardOptions={accountCardOptions?.length ? accountCardOptions : localAccountCardOptions}
                />

                {/* VAT reporting: action choice + primary button */}
                <Box dir={direction} sx={{ mt: 2, direction: direction || 'ltr' }}>
                    <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                        <FormLabel component="legend" sx={{ mb: 1 }}>
                            {t('VatReportActionLabel') || 'Reporting action'}
                        </FormLabel>
                        <RadioGroup
                            row
                            value={String(vatReportAction)}
                            onChange={(e) => handleVatReportActionChange(Number(e.target.value))}
                            sx={{ flexWrap: 'wrap', direction: direction || 'ltr', justifyContent: 'center'}}
                            dir={direction || 'ltr'}
                        >
                            <FormControlLabel value="1" control={<Radio />} label={t('VatReportActionReport')} />
                            <FormControlLabel value="2" control={<Radio />} label={t('VatReportActionCheck')} />
                            <FormControlLabel value="3" control={<Radio />} label={t('VatReportActionCheckAndReportIfCorrect')} />
                        </RadioGroup>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={handleSendVatDetailedReport}
                        disabled={sendVatReportLoading || !caseId || !accountantId || !startDate || !endDate}
                        sx={{
                            borderRadius: '8px',
                            backgroundColor: '#304FFF',
                            '&:hover': { backgroundColor: '#1565c0' }
                        }}
                    >
                        {sendVatReportLoading ? (t('Sending') || 'Sending...') : getVatReportButtonLabel()}
                    </Button>
                </Box>

                {/* Footer Actions */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: 2, 
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #dee2e6'
                }}>
                    <Button 
                        variant="outlined" 
                        onClick={onClose}
                        sx={{
                            borderRadius: '8px',
                            borderColor: '#6c757d',
                            color: '#6c757d',
                            '&:hover': {
                                borderColor: '#495057',
                                backgroundColor: 'rgba(108, 117, 125, 0.04)'
                            }
                        }}
                    >
                        {t('Close')}
                    </Button>
                    {((pcnHighConfirmationDocs && pcnHighConfirmationDocs.length > 0) || 
                      (pcnDocumentsNeedingAllocation && pcnDocumentsNeedingAllocation.length > 0) ||
                      (pcnDocumentsWithoutReference && pcnDocumentsWithoutReference.length > 0) ||
                      (pcnDocumentsOutOfRange && pcnDocumentsOutOfRange.length > 0)) && (
                        <Button 
                            variant="contained" 
                            onClick={() => {
                                onClose();
                                setShowPCNModal(true);
                                handlePCNDownload(startDate, endDate);
                            }}
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#304FFF',
                                '&:hover': {
                                    backgroundColor: '#1565c0'
                                }
                            }}
                        >
                            {t('DownloadPCNReport')}
                        </Button>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default PCNChecksModal;
